/*
 * @Author: Yumeng Xue
 * @Date: 2024-12-01 01:10:01
 * @LastEditTime: 2026-02-03 00:19:23
 * @LastEditors: Yumeng Xue
 * @Description: 高性能 L2P（Line-to-Pixel）索引引擎，
 * 用于线图分箱，支持 TDA 扫描器。
 * @FilePath: /topo-mess/src/core/binning.ts
 */
import { RoaringBitmap32 } from "roaring-wasm";
import { NormalVec2, Vec2 } from "./math/vector";
import * as d3 from 'd3';

// -----------------------------------------------------------------------------
// 1. 辅助类型和函数
// -----------------------------------------------------------------------------

/**
 * 线条数据的标准接口
 */
export interface LineData {
    times?: number[]; // 可选的时间戳，用于区分 2D 轨迹
    xValues: number[];
    yValues: number[];
    sid?: string;
    label?: string;
    state?: string;
    latitude?: number | null;
    longitude?: number | null;
    elevation?: number | null;
}

/**
 * 描述一条线穿过的 Bin 序列（用于调试或特定分析）
 */
export type LineBinSequence = { x: number, y: number, angleVec: NormalVec2 }[];

/**
 * 使用基于整数的 Bresenham 风格算法遍历 2D 像素。
 * @param start - [x0, y0] 起点坐标
 * @param end - [x1, y1] 终点坐标
 * @returns [x, y] 像素坐标元组的数组
 */
function pixelTraversal2D(start: [number, number], end: [number, number]): [number, number][] {
    const [x0, y0] = start;
    const [x1, y1] = end;

    let currentX = Math.round(x0);
    let currentY = Math.round(y0);
    const targetX = Math.round(x1);
    const targetY = Math.round(y1);

    const traversedPixels: [number, number][] = [[currentX, currentY]];

    if (currentX === targetX && currentY === targetY) {
        return traversedPixels;
    }

    const dx = Math.abs(targetX - currentX);
    const dy = Math.abs(targetY - currentY);
    const stepX = targetX > currentX ? 1 : -1;
    const stepY = targetY > currentY ? 1 : -1;

    let error = dx - dy;
    const maxSteps = dx + dy + 1;
    let stepCount = 0;

    while ((currentX !== targetX || currentY !== targetY) && stepCount < maxSteps) {
        const error2 = error * 2;

        if (error2 > -dy) {
            error -= dy;
            currentX += stepX;
        }
        if (error2 < dx) {
            error += dx;
            currentY += stepY;
        }

        traversedPixels.push([currentX, currentY]);
        stepCount++;

        if (stepCount >= maxSteps) {
            console.warn("Bresenham max steps reached, possible incomplete traversal.");
            break;
        }
    }
    return traversedPixels;
}


// -----------------------------------------------------------------------------
// 2. 核心 Bin 类
// -----------------------------------------------------------------------------

/**
 * Bin (单元格) 类
 * 这是一个“精简”但“智能”的 Bin。它只聚合 TDA 扫描器
 * 和矢量场分析所需的最少信息，以保证高性能和低内存占用。
 */
export class Bin {
    x: number;
    y: number;

    /** * TDA 扫描器需要的通道信息 
     */
    passages: Map<number, Set<number>>;

    /** * 密度值
     */
    density: number;

    /** * 关键数据 4: Line IDs
     * 修正: 使用普通数组进行构建，避免 O(N^2) 复制。
     * 修正: 使用 number 避免 Int16 溢出 (支持 >32767 条线)。
     * 注意：这里存储的 ID 是有序的（按加入顺序），这对于后续双指针算法至关重要。
     */
    lineIds: number[];

    /** * 关键数据 5: Structure Tensors (SoA 布局)
     * 修正: 不再存储 Float64Array 对象，而是拆分为三个普通数组。
     * 这种 "Structure of Arrays" 布局对 CPU 缓存更友好，且无 GC 压力。
     */
    Jxx: number[];
    Jxy: number[];
    Jyy: number[];

    constructor(x: number, y: number) {
        this.x = x;
        this.y = y;
        this.passages = new Map();
        this.density = 0;

        // 初始化为空数组
        this.lineIds = [];
        this.Jxx = [];
        this.Jxy = [];
        this.Jyy = [];
    }

    addPassage(
        lineId: number,
        segmentIndex: number,
        kernelValue: number,
        angleVec: NormalVec2
    ) {
        // 1. 注册通道 (TDA Arc Length Lookup)
        if (!this.passages.has(lineId)) {
            this.passages.set(lineId, new Set());
        }
        this.passages.get(lineId)!.add(segmentIndex);

        // 2. 计算当前传入的张量分量 (带 Kernel 权重)
        // J = v * v^T * weight
        const valXX = angleVec.x * angleVec.x * kernelValue;
        const valXY = angleVec.x * angleVec.y * kernelValue;
        const valYY = angleVec.y * angleVec.y * kernelValue;

        // 3. 聚合逻辑 (Check & Aggregate)
        const count = this.lineIds.length;

        // 检查最后一条记录是否是当前线 (处理 Kernel 扩散或自交导致的连续进入)
        // 注意：因为 Binning 是按 Line 顺序执行的，同一条线的访问必然是连续的
        if (count > 0 && this.lineIds[count - 1] === lineId) {
            // [CASE A] 同一条线多次贡献 -> 累加到现有记录
            this.Jxx[count - 1] += valXX;
            this.Jxy[count - 1] += valXY;
            this.Jyy[count - 1] += valYY;
        } else {
            // [CASE B] 新的线进入 -> 追加新记录
            this.lineIds.push(lineId);
            this.Jxx.push(valXX);
            this.Jxy.push(valXY);
            this.Jyy.push(valYY);
        }

        // 4. 聚合总密度
        this.density += kernelValue;
    }

    /**
     * 辅助函数：获取所有穿过此 Bin 的唯一 lineId
     */
    getLineIds(): number[] {
        return Array.from(this.passages.keys());
    }

    /**
     * 辅助函数：获取 RoaringBitmap 格式的 lineId
     * (注意：在 addPassage 中我们不再使用 RoaringBitmap 来存储 ID，
     * 因为 Map<number, Set<number>> 已经包含了这些信息。
     * 但我们可以按需 *创建* 一个。)
     */
    getLineIdSet_Roaring(): RoaringBitmap32 {
        const set = new RoaringBitmap32();
        for (const lineId of this.passages.keys()) {
            set.add(lineId);
        }
        return set;
    }
}


// -----------------------------------------------------------------------------
// 3. Binning 引擎
// -----------------------------------------------------------------------------

export class Binning {
    width: number;
    height: number;
    private bins: Bin[][];
    private binSize: [number, number];
    private xBinCount: number;
    private yBinCount: number;
    private totalLineNum: number;

    public lineBinTraces: Int32Array[];
    public lineBinSequences: LineBinSequence[] = [];

    constructor(width: number, height: number, binningXStep: number, binningYStep: number) {
        // ... (构造函数保持不变) ...
        if (binningXStep <= 0 || binningYStep <= 0 || width <= 0 || height <= 0) {
            throw new Error(`无效的分箱配置`);
        }
        this.width = width;
        this.height = height;
        this.binSize = [binningXStep, binningYStep];
        this.totalLineNum = 0;
        this.lineBinTraces = [];
        this.xBinCount = Math.ceil(this.width / this.binSize[0]);
        this.yBinCount = Math.ceil(this.height / this.binSize[1]);
        this.bins = new Array(this.xBinCount).fill(0).map((_, x) =>
            new Array(this.yBinCount).fill(0).map((_, y) => new Bin(x, y))
        );
    }


    public getBins(): Bin[][] { return this.bins; }
    public getBinSize(): [number, number] { return this.binSize; }
    public getTotalLineNum(): number { return this.totalLineNum; }
    public getBinCounts(): [number, number] {
        return [this.xBinCount, this.yBinCount];
    }


    executeBinning(lines: LineData[], kernel: number[][]): LineBinSequence[] {
        if (lines.length === 0) return [];
        this.totalLineNum = lines.length;
        const lineBinSequences = this._executeBinningInternal(lines, kernel);
        this.lineBinSequences = lineBinSequences;
        return lineBinSequences.filter(seq => seq.length > 0);
    }

    private _executeBinningInternal(lines: LineData[], kernel: number[][]): LineBinSequence[] {
        const kernelRadius = Math.floor(kernel.length / 2);
        const lineBinSequences: LineBinSequence[] = [];
        const [binWidth, binHeight] = this.binSize;

        this.lineBinTraces = new Array(lines.length);

        for (let index = 0; index < lines.length; index++) {
            const line = lines[index];
            const lineBinSequence: LineBinSequence = [];

            // 使用 Set 来确保 Trace 里的 Bin ID 不重复? 
            // 不，Bresenham 生成的路径是有序且可能有重复的(如果Kernel大)
            // 但为了 TdaScanner 的性能，我们在 Trace 里通常只记录 "主路径"
            // 这里我们只记录中心像素的路径，不记录 Kernel 扩散的路径
            const currentLineTrace: number[] = [];

            // 为了避免同一条线在同一个 Bin 记录多次 Trace ID (导致后续计算重复加权)
            // 我们记录上一个压入的 BinID
            let lastPushedBinID = -1;

            for (let i = 1; i < line.xValues.length; i++) {
                let t0 = line.xValues[i - 1];
                let t1 = line.xValues[i];
                let y0 = line.yValues[i - 1];
                let y1 = line.yValues[i];

                const normalVector = new NormalVec2(t1 - t0, y1 - y0);
                // 确保方向一致性 (半球空间)
                if (normalVector.x < 0) {
                    normalVector.x = -normalVector.x;
                    normalVector.y = -normalVector.y;
                }
                if (normalVector.x === 0 && normalVector.y < 0) {
                    normalVector.y = -normalVector.y;
                } else if (normalVector.y === 0 && normalVector.x < 0) {
                    normalVector.x = -normalVector.x;
                }

                const pixels = pixelTraversal2D([t0, y0], [t1, y1]);
                if (pixels.length < 2) continue;

                for (let j = 0; j < pixels.length; j++) {
                    const [px, py] = pixels[j];
                    if (px < 0 || px > this.width || py < 0 || py > this.height || (i > 1 && j === 0)) continue;

                    const x = Math.floor(px / binWidth);
                    const y = Math.floor(py / binHeight);
                    if (x < 0 || x >= this.xBinCount || y < 0 || y >= this.yBinCount) continue;

                    // 生成 Bin ID (Row-Major: y * Width + x, 或者是 Col-Major)
                    // 只要和 TdaScanner 统一即可。通常用: x * yCount + y (Col-Major) 或 y * xCount + x
                    // 这里假设用 Col-Major 以匹配之前的代码习惯:
                    const binID = x * this.yBinCount + y;

                    // 优化 Trace: 如果 Bresenham 只有一步且在同一个 Bin，不要重复 push
                    if (binID !== lastPushedBinID) {
                        currentLineTrace.push(binID);
                        lastPushedBinID = binID;
                    }

                    lineBinSequence.push({ x, y, angleVec: normalVector });

                    // TraceIndex: 指向这是 trace 中的第几个 bin
                    // 注意：因为上面做了去重 check，这里的 index 对应去重后的
                    const traceIndex = currentLineTrace.length - 1;

                    // Kernel 扩散
                    for (let k = 0; k < kernel.length; k++) {
                        for (let l = 0; l < kernel[k].length; l++) {
                            const xIndex = x + k - kernelRadius;
                            const yIndex = y + l - kernelRadius;
                            const kernelValue = kernel[k][l];

                            if (xIndex >= 0 && xIndex < this.xBinCount && yIndex >= 0 && yIndex < this.yBinCount) {
                                this.bins[xIndex][yIndex].addPassage(
                                    index,
                                    traceIndex,
                                    kernelValue,
                                    normalVector
                                );
                            }
                        }
                    }
                }
            }
            this.lineBinTraces[index] = Int32Array.from(currentLineTrace);
            lineBinSequences.push(lineBinSequence);
        }
        return lineBinSequences;
    }
}
